import moment from 'moment';
import {LeanDocument, Types} from 'mongoose';
import {RawLocation} from 'vue-router/types/router';
import {ToDoManagerApi} from '@/classes/api/to-do-manager.api.class';
import {ToDoItemDocument} from '@/documentTypes/to-do-manager/ToDoItem.document';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {ToDoDataTypesEnum} from '@/enums/to-do-manager/ToDoDataTypes.enum';
import {isPopulated, retrieveObjectId} from '@/helpers/isObjectId.helper';
import {extractName} from '@/helpers/extractName';
import {isToDoOverdue, toDoDeadline} from '@/helpers/toDoDue.helper';

/* Data and grouping of the /todos page (open and completed to-dos of the logged-in user) */

export type ToDoRowItem = LeanDocument<ToDoItemDocument>;

export type ToDoEntityInfo = { icon: string; link?: RawLocation; name: string; kind: string };

export type OpenGroupKey = 'overdue' | 'today' | 'week' | 'later' | 'none';
export type CompletedGroupKey = 'today' | 'yesterday' | 'week' | 'older';
export type ToDoGroup = { key: string; name: string; tone: string; items: ToDoRowItem[] };

const USER_POPULATE = {
  fields: ['meta', 'type'],
  populate: {meta: {fields: ['firstName', 'lastName', 'profileImg']}},
};

/**
 * Loads all to-dos the user is responsible for or created (as the old tables did: two queries, merged without duplicates).
 * Completed ones only of the last two weeks.
 */
export async function loadToDos(loginUserId: Types.ObjectId | string, completed: boolean): Promise<ToDoRowItem[]> {
  const completedFilter = completed ? {
    completedAtBetween: {
      start: moment().startOf('day').subtract(2, 'weeks').toDate(),
      end: moment().endOf('day').toDate(),
    },
  } : {};
  const query = (filter: Record<string, unknown>) => ToDoManagerApi.items.find({
    filter: {...filter, completed, ...completedFilter},
    populate: {
      createdBy: USER_POPULATE,
      responsible: USER_POPULATE,
      entity: {fields: ['firstName', 'lastName', 'profileImg', 'name']},
    },
    skipPagination: true,
  } as any);
  const responses = await Promise.all([query({responsible: [loginUserId]}), query({createdBy: [loginUserId]})]);
  return (responses.map((resp) => resp.items).flat() as ToDoRowItem[]).unique({key: '_id'});
}

export function toDoEntity(item: ToDoRowItem): ToDoEntityInfo {
  const name = isPopulated(item.entity) ? extractName(item.entity) : 'Unbekannt';
  const id = retrieveObjectId(item.entity as any);
  switch (item.entityType) {
    case CrudEntityTypes.INSTITUTION:
      return {icon: 'mdi-domain', link: `/institutions/${id}`, name, kind: 'Einrichtung'};
    case CrudEntityTypes.EMPLOYEE:
      return {icon: 'mdi-human-male-board', link: `/employees/${id}`, name, kind: 'Mitarbeiter:in'};
    case CrudEntityTypes.STUDENT:
      return {icon: 'mdi-school', link: `/students/${id}`, name, kind: 'Schüler:in'};
    case CrudEntityTypes.PARENT:
      return {icon: 'mdi-account-child', link: `/parents/${id}`, name, kind: 'Elternteil'};
    default:
      return {icon: 'mdi-help', link: undefined, name, kind: ''};
  }
}

/** Name of a populated login user (createdBy / responsible) */
export function loginUserName(user: unknown): string {
  const meta = user && isPopulated(user as any) ? (user as any).meta : null;
  return meta && isPopulated(meta) ? extractName(meta) : '';
}

export function sameId(a: unknown, b: unknown): boolean {
  return !!a && !!b && String(retrieveObjectId(a as any)) === String(retrieveObjectId(b as any));
}

export function openGroupKey(item: ToDoRowItem, now: moment.Moment = moment()): OpenGroupKey {
  if (!item.dueDate) {
    return 'none';
  }
  if (isToDoOverdue(item, now)) {
    return 'overdue';
  }
  if (now.isSame(item.dueDate, 'day')) {
    return 'today';
  }
  return moment(item.dueDate).isSameOrBefore(now.clone().endOf('isoWeek')) ? 'week' : 'later';
}

export function completedGroupKey(item: ToDoRowItem, now: moment.Moment = moment()): CompletedGroupKey {
  const at = moment(item.completedAt);
  if (at.isSame(now, 'day')) {
    return 'today';
  }
  if (at.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return 'yesterday';
  }
  return at.isSame(now, 'isoWeek') ? 'week' : 'older';
}

const OPEN_GROUPS: Array<[OpenGroupKey, string, string]> = [
  ['overdue', 'Überfällig', 'tone-bad'],
  ['today', 'Heute', 'tone-warn'],
  ['week', 'Diese Woche', 'tone-info'],
  ['later', 'Später', 'tone-muted'],
  ['none', 'Ohne Fälligkeit', 'tone-muted'],
];

const COMPLETED_GROUPS: Array<[CompletedGroupKey, string, string]> = [
  ['today', 'Heute erledigt', 'tone-good'],
  ['yesterday', 'Gestern', 'tone-good'],
  ['week', 'Diese Woche', 'tone-muted'],
  ['older', 'Früher', 'tone-muted'],
];

/** Groups sorted items, keeping the order inside each group; empty groups are left out */
export function groupToDos(items: ToDoRowItem[], completed: boolean): ToDoGroup[] {
  const now = moment();
  const definitions = completed ? COMPLETED_GROUPS : OPEN_GROUPS;
  const keyOf = (item: ToDoRowItem): string => (completed ? completedGroupKey(item, now) : openGroupKey(item, now));
  return definitions
    .map(([key, name, tone]) => ({key, name, tone, items: items.filter((item) => keyOf(item) === key)}))
    .filter((group) => group.items.length > 0);
}

export type ToDoSort = 'due' | 'name' | 'created' | 'completed';

function time(value: unknown): number {
  return value ? new Date(value as Date).getTime() : 0;
}

export function sortToDos(items: ToDoRowItem[], sort: ToDoSort): ToDoRowItem[] {
  const list = [...items];
  switch (sort) {
    case 'name':
      return list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
    case 'created':
      return list.sort((a, b) => time(b.createdAt) - time(a.createdAt));
    case 'completed':
      return list.sort((a, b) => time(b.completedAt) - time(a.completedAt));
    default:
      return list.sort((a, b) => {
        const deadlineA = toDoDeadline(a);
        const deadlineB = toDoDeadline(b);
        if (!deadlineA || !deadlineB) {
          return (deadlineA ? 0 : 1) - (deadlineB ? 0 : 1);
        }
        return time(a.dueDate) - time(b.dueDate);
      });
  }
}

/** Answer of a completed to-do as text */
export function toDoDataText(item: ToDoRowItem): string {
  const data = item.data as boolean | Date | string | string[] | number | undefined | null;
  if (data === undefined || data === null || data === '') {
    return '';
  }
  if (typeof data === 'boolean') {
    return data ? 'Ja' : 'Nein';
  }
  if (item.dataType === ToDoDataTypesEnum.DATE) {
    return moment(data as Date).format('DD.MM.YYYY');
  }
  return Array.isArray(data) ? data.join(', ') : String(data);
}
